import { ClientInformation } from './client-information';

/**
 * Single metric value count for a contract, e.g. { metricName: 'channel', value: 'Retail', columnName: 'Channel.Retail', count: 12 }
 * Mirrors ContractMetricValueWebModel
 */
export class ContractMetricValue {
  metricName: string;
  value: string;
  columnName: string;
  count: number;

  constructor(init?: Partial<ContractMetricValue>) {
    this.metricName = init?.metricName ?? '';
    this.value = init?.value ?? '';
    this.columnName = init?.columnName ?? '';
    this.count = init?.count ?? 0;
  }
}

/**
 * One client contract aggregated across every billed invoice period overlapping the requested date range.
 * Mirrors ContractMetricExportWebModel (contract + destination details only, client details are looked up
 * separately by clientInformationId). Every record returned from the same request shares the same
 * ordered metricValues, so the first record can be used to build the grid columns.
 */
export class ContractMetricExport {
  clientContractId: string;
  clientInformationId: string;
  sfCustomerNumber: string;
  identifierIdValue: string;
  identifierNameValue: string;
  destinationEndpointId: number;
  endpointName: string;
  endpointDisplayName: string;
  dateStart: Date;
  dateEnd: Date;
  invoicePeriodCount: number;
  totalLoanCount: number;
  loanVolume: number;
  /** when the metric summary (materialized view) backing this record was refreshed, null when calculated live */
  summaryRefreshedAt: Date | null;
  metricValues: ContractMetricValue[];

  /**
   * Flattened metric counts for grid binding, keyed by {@link ContractMetricExport.getMetricFieldKey}.
   * Metric values can contain '.', spaces, '@', etc. so an index based key is used instead of the value
   * (grid fields are resolved as dot separated paths).
   */
  metricCounts: { [key: string]: number } = {};

  //#region Client Information (not returned by the API, populated client-side with applyClientInformation)
  clientFullName = '';
  clientShortName = '';
  losTalkerClientId: number | null = null;
  parentCompany = '';
  //#endregion

  constructor(init?: Partial<ContractMetricExport>) {
    this.clientContractId = init?.clientContractId;
    this.clientInformationId = init?.clientInformationId;
    this.sfCustomerNumber = init?.sfCustomerNumber ?? '';
    this.identifierIdValue = init?.identifierIdValue ?? '';
    this.identifierNameValue = init?.identifierNameValue ?? '';
    this.destinationEndpointId = init?.destinationEndpointId;
    this.endpointName = init?.endpointName ?? '';
    this.endpointDisplayName = init?.endpointDisplayName || this.endpointName;
    this.invoicePeriodCount = init?.invoicePeriodCount ?? 0;
    this.totalLoanCount = init?.totalLoanCount ?? 0;
    this.loanVolume = init?.loanVolume ?? 0;
    this.dateStart = init?.dateStart != null ? new Date(init.dateStart) : null;
    this.dateEnd = init?.dateEnd != null ? new Date(init.dateEnd) : null;
    this.summaryRefreshedAt = init?.summaryRefreshedAt != null ? new Date(init.summaryRefreshedAt) : null;

    this.metricValues = (init?.metricValues ?? []).map(v => new ContractMetricValue(v));
    this.metricValues.forEach((v, i) => {
      this.metricCounts[ContractMetricExport.getMetricFieldKey(i)] = v.count;
    });
  }

  /** Copies client details from a separately loaded ClientInformation record (matched on clientInformationId) */
  applyClientInformation(client: ClientInformation | null | undefined): ContractMetricExport {
    this.clientFullName = client?.clientFullName ?? '';
    this.clientShortName = client?.clientShortName ?? '';
    this.losTalkerClientId = client?.losTalkerClientId ?? null;
    this.parentCompany = client?.parentCompany ?? '';
    return this;
  }

  static getMetricFieldKey(index: number): string {
    return 'm' + index;
  }

  static getMetricGridField(index: number): string {
    return 'metricCounts.' + ContractMetricExport.getMetricFieldKey(index);
  }
}
